import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import bulkAddToQuickOrder from "@store/Store/CommonHandlers/BulkAddToQuickOrder";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { TYPOGRAPHY } from "@store/theme/design";
import parse from "html-react-parser";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface ProductLine {
    productNumber?: string;
    quantity?: number;
    raw?: string;
    formatValidation?: string;
    result?: ProductAutocompleteItemModel;
    resultError?: string;
}

const enum fields {
    copyPasteDirections = "copyPasteDirections",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.copyPasteDirections]: string;
    };
}

const mapDispatchToProps = {
    bulkAddToQuickOrder,
};

const mapStateToProps = (state: StoreApplicationState) => {
    const bulkAddToCartState = state.store.data.bulkAddToCart;
    return {
        isLoading: bulkAddToCartState.isLoading,
    };
};

export interface QuickOrderPadStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    subText?: TypographyPresentationProps;
    buttonAddRows?: LinkPresentationProps;
    copyPasteTextArea?: TextAreaProps;
    buttonAddToCart?: ButtonPresentationProps;
}
export const quickOrderPadStyles:QuickOrderPadStyles = {
    container: {
        gap: 5,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    bottomGridItem: {
        width: 12,
        css: css`
            flex-direction: row;
            justify-content: flex-end;
        `,
    },
    subText: {
        size: TYPOGRAPHY.size.smallest,
    },
    buttonAddToCart: {
        css: css`
            ${({ theme }) => theme?.button?.primary?.css}
            padding-top: 0;
            padding-bottom: 0;
            min-height: 48px;
            line-height: 18px;
        `,
    },
    copyPasteTextArea: {
        css: css`
            resize: vertical;
        `,
    },
}

const styles = quickOrderPadStyles;

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const QuickOrderPad: FC<Props> = props => {
    const toasterContext = useContext(ToasterContext);
    const {fields, isLoading, bulkAddToQuickOrder} = props;
    const [copyPasteString, setCopyPasteString] = useState("");
    const [copyPasteErrorMessage, setCopyPasteErrorMessage] = useState("");

    const onChangePasteOrder = (event: React.FormEvent<HTMLTextAreaElement>) => {
        setCopyPasteErrorMessage("");
        setCopyPasteString(event.currentTarget.value);
    };

    const validateCopyPasteString = () => {
        const rgx = /^([a-zA-Z0-9\-]+)[\t, ]+([\d*\.?\,?\d*]+[\d*\.?\,?\d*]*)[\s]*$/;
        const lines = copyPasteString.split(/\r\n|\r|\n/);
        let isValid = true;
        let errorMessage = "";

        const productLinesTmp: ProductLine[] = [];
        lines.forEach((line, index) => {
            const match = rgx.exec(line);

            if (match) {
                if (match[2] && match[2].length > 4) {
                    productLinesTmp.push({productNumber: match[1].toUpperCase(), quantity: Number(match[2].replace(/,/g, ''))});
                } else {
                    productLinesTmp.push({productNumber: match[1].toUpperCase(), quantity: parseInt(match[2], 10)});
                }
            } else {
                const matchEmpty = /^[ \t]*$/.exec(line);
                if (matchEmpty) {
                    return;
                }
                productLinesTmp.push({raw: line});
                isValid = false;
                errorMessage += `${(errorMessage===""?`${translate("Invalid format for line(s)")}:<br/>`:"<br/>")+line}`;
            }
        });
        if (!isValid) {
            setCopyPasteErrorMessage(errorMessage);
            return [];
        }
        return productLinesTmp;
    };

    const onClickAddToCart = () => {
        const productLinesTmp = validateCopyPasteString();
        let errorMessage = "";
        if (productLinesTmp && productLinesTmp.length > 0) {
            bulkAddToQuickOrder({
                apiParameter: {
                    productLines: productLinesTmp, 
                },
                onComplete: (result) => {
                    if (!result.apiResult?.productAllLoaded) {
                        toasterContext.addToast({
                            body: <Typography>{siteMessage("QuickOrder_errorOccured")}</Typography>,
                            messageType: "warning",
                        });
                    } else if (result.apiResult?.message !== "") {
                        errorMessage += result.apiResult?.message;
                        setCopyPasteErrorMessage(errorMessage);
                        toasterContext.addToast({
                            body: <Typography>{siteMessage("ImportQuote_ProductsImportedComplete", `${result.apiResult.nbAdded}`, `${result.apiResult.nbTotal}`)}</Typography>,
                            messageType: "success",
                        });
                    } else {
                        toasterContext.addToast({
                            body: <Typography>{siteMessage("ImportQuote_ProductsImportedComplete", `${result.apiResult.nbAdded}`, `${result.apiResult.nbTotal}`)}</Typography>,
                            messageType: "success",
                        });
                    }
                },
                onSegmentComplete: (step, nbComplete, nbProcessed, nbTotal) => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage(step, `${nbProcessed}`, `${nbComplete}`)}</Typography>,
                        messageType: "success",
                    });
                },
            });
        }
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItem}>
                    <TextArea
                        {...styles.copyPasteTextArea}
                        label={translate("Copy & Paste Order")}
                        value={copyPasteString}
                        onChange={onChangePasteOrder}
                        error={copyPasteErrorMessage ? parse(copyPasteErrorMessage, parserOptions) : ""}
                    />
                </GridItem>
                <GridItem {...styles.bottomGridItem}>
                    {fields.copyPasteDirections && (
                        <Typography as="p" {...styles.subText}>
                            {parse(fields.copyPasteDirections, parserOptions)}
                        </Typography>
                    )}
                </GridItem>
                <GridItem {...styles.bottomGridItem}>
                    <Button
                        onClick={onClickAddToCart}
                        {...styles.buttonAddToCart}
                        disabled={!copyPasteString || copyPasteString === ""}
                    >
                        {translate("Add All To Order")}
                    </Button>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderPad),
    definition: {
        displayName: "Quick Order Pad",
        group: "Quick Order",
        allowedContexts: ["QuickOrderPage"],
        fieldDefinitions: [
            {
                name: fields.copyPasteDirections,
                displayName: "Copy & Paste Directions",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
